/**
 * @fileoverview Firms tab of the billing admin (MOR-438)
 *
 * Every firm with its billing plan, Stripe state and revenue to date.
 * Search + plan filter on top; rows drill into the firm manage dialog.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/primitives/card";
import { Input } from "@/ui/primitives/input";
import { Skeleton } from "@/ui/primitives/skeleton";
import { Building2, ChevronRight, Search } from "lucide-react";
import { formatAudExact, formatDate, type Overview } from "./billingFormat";
import { EmptyState, PlanBadge, StripeStatusPill } from "./billingShared";

const PLAN_FILTERS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All plans" },
  { value: "pilot", label: "Pilot" },
  { value: "subscription", label: "Subscription" },
  { value: "per_job", label: "Per Job" },
];

export function FirmsTab({
  onManage,
}: {
  onManage: (orgId: Id<"organizations">) => void;
}) {
  const { data: overview, isPending } = useQuery(
    convexQuery(api.admin.billing.getBillingOverview, {}),
  );
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all");

  const filtered = useMemo(() => {
    if (!overview) return [];
    const q = search.trim().toLowerCase();
    return overview.orgs.filter((org) => {
      if (planFilter !== "all" && org.plan !== planFilter) return false;
      if (q && !org.orgName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [overview, search, planFilter]);

  if (isPending || !overview) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-10 rounded-lg" />
        <Skeleton className="h-[320px] rounded-xl" />
      </div>
    );
  }

  const paying = overview.orgs.filter((o) => o.plan !== "pilot").length;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Firm billing plans</CardTitle>
            <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">
              Every firm starts on the Pilot Program and is never charged until
              moved to a Subscription or Per-Job plan. Moving off pilot starts
              the affiliate fee clock.
            </p>
          </div>
          <span className="text-xs text-muted-foreground">
            {overview.orgs.length} firms · {paying} paying
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search firms…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64 pl-8"
            />
          </div>
          <Select value={planFilter} onValueChange={setPlanFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PLAN_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(search || planFilter !== "all") && (
            <span className="text-xs text-muted-foreground">
              {filtered.length} of {overview.orgs.length} shown
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Firm</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead className="text-right">Monthly fee</TableHead>
              <TableHead>Converted</TableHead>
              <TableHead>Affiliate</TableHead>
              <TableHead>Stripe</TableHead>
              <TableHead className="text-right">Charged (ex GST)</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((org) => (
              <FirmRow key={org.orgId} org={org} onManage={onManage} />
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <EmptyState
                    icon={<Building2 className="h-8 w-8" />}
                    title={
                      overview.orgs.length === 0
                        ? "No organisations yet"
                        : "No firms match the current filters"
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function FirmRow({
  org,
  onManage,
}: {
  org: Overview["orgs"][number];
  onManage: (orgId: Id<"organizations">) => void;
}) {
  return (
    <TableRow
      className="group cursor-pointer"
      onClick={() => onManage(org.orgId)}
    >
      <TableCell>
        <div className="flex items-center gap-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
            {org.orgName.slice(0, 1).toUpperCase()}
          </span>
          <span className="font-medium">{org.orgName}</span>
        </div>
      </TableCell>
      <TableCell>
        <PlanBadge plan={org.plan} />
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {org.subscriptionMonthlyExGstAudCents !== null ? (
          formatAudExact(org.subscriptionMonthlyExGstAudCents)
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
        {org.convertedAt ? formatDate(org.convertedAt) : "—"}
      </TableCell>
      <TableCell className="text-sm">
        {org.affiliateAgreementName ?? (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        <StripeStatusPill
          stripeCustomerId={org.stripeCustomerId}
          stripeSubscriptionStatus={org.stripeSubscriptionStatus}
        />
      </TableCell>
      <TableCell className="text-right font-medium tabular-nums">
        {org.chargedExGstAudCents > 0 ? (
          formatAudExact(org.chargedExGstAudCents)
        ) : (
          <span className="font-normal text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="w-8 text-right">
        <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 group-hover:text-muted-foreground" />
      </TableCell>
    </TableRow>
  );
}
